import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { Wizard } from '@bingads-webui-fluent-component/wizard';
import { getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { getFlow } from '@bingads-webui-campaign-react/linkedin-utils';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { mergeClasses } from '@fluentui/react-components';

import { useInitializeWizard } from './use-initialize-wizard';
import { LinkedInStepConfig } from './steps/step-config';
import { MmpFetchProvider } from './wizard-components/mmp-provider';
import { CustomLayoutContainer } from './wizard-components/custom-layout-container';
import { useWizardStyles } from './styles/wizard-styles';

export const LinkedInCampaignWizard = (props) => {
  const {
    perfMarker, siteMap, scope,
  } = props;
  const classes = useWizardStyles();

  const instrumentation = useContext(InstrumentationContext);

  const routeToNavigateToWhenDone = getRoutePathById(siteMap, 'campaigns');

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild('LinkedInCampaignWizard')
  ), [perfMarker]);

  const {
    isInitialized,
    initialStep,
    initialFlowFactors,
    initialWizardData,
  } = useInitializeWizard();

  const config = useMemo(() => ({
    id: 'linkedin-campaign-wizard',
    steps: LinkedInStepConfig,
    getFlow,
    initialStep,
    initialFlowFactors,
    initialWizardData,
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: ({ wizardData }) => {
        if (wizardData.onExited) {
          wizardData.onExited();
          return;
        }
        scope.save(routeToNavigateToWhenDone);
      },
    },
  }), [initialFlowFactors, initialStep, initialWizardData, routeToNavigateToWhenDone, scope]);

  if (!isInitialized) {
    return null;
  }

  return (
    <div className={mergeClasses('linkedin-campaign-wizard', classes.wizardContainer)}>
      <PerformanceMarkerProvider
        name="LinkedInCampaignWizard"
        instrumentation={instrumentation}
        immediateDone={false}
      >
        <Scenario name="LinkedInCampaignCreationWizard">
          <Activity name="DefaultWizardActivity">
            <Wizard config={config}>
              <WizardPerfMarker wizardName="LinkedInCampaignWizard" wizardMarker={wizardPerfMarker}>
                <MmpFetchProvider>
                  <CustomLayoutContainer />
                </MmpFetchProvider>
              </WizardPerfMarker>
            </Wizard>
          </Activity>
        </Scenario>
      </PerformanceMarkerProvider>
    </div>
  );
};

LinkedInCampaignWizard.propTypes = {
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  siteMap: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
};
